import { useCallback, useEffect, useMemo, useState } from 'react';

import { UseFormReturn, useForm } from '@kanda-libs/ks-component-ts';
import { StringIndexedObject } from '@kanda-libs/ks-design-library';
import { InfoAuth, UserType, actions } from '@kanda-libs/ks-frontend-services';

import { APP_ENV } from 'common/constants';
import { useAppDispatch } from 'components/shared/App';
import useApiError from 'hooks/useApiError';

import { Destination } from './constants';

export type UsersModalDisplay = 'users' | 'code';

export interface UserModalHook {
  form: UseFormReturn;
  onSubmit: (vals: StringIndexedObject) => void;
  url: string;
  isSubmitting: boolean;
}

const getContinueUrl = (destination: Destination) => {
  const baseUrl = `https://dashboard${
    APP_ENV === 'qa' ? '-qa' : ''
  }.ideal4finance.co.uk`;
  if (destination === 'dashboard') return baseUrl;
  if (destination === 'contract') return `${baseUrl}/setup/subscription`;
  if (destination === 'subscription') return `${baseUrl}/setup/subscription`;
  if (destination === 'premium_credit_enable')
    return `${baseUrl}/setup/subscription/enable-premium-credit`;
  if (destination === 'premium_credit_disable')
    `${baseUrl}/setup/subscription/disable-premium-credit`;
  return baseUrl;
};

export default function useUserModal(
  user: UserType | undefined,
): UserModalHook {
  const [url, setUrl] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const dispatch = useAppDispatch();
  const onError = useApiError('Error getting login URL');

  const form = useForm();

  const onSubmit = useCallback(
    (vals: StringIndexedObject) => {
      if (!user) return;
      const continueUrl = getContinueUrl(vals.destination);
      const body: InfoAuth = {
        email: user.email,
        continue_url: continueUrl,
      };
      setIsSubmitting(true);
      dispatch(
        actions.infoAuth({
          body,
          onError: (err) => {
            setIsSubmitting(false);
            onError(err);
          },
          onSuccess: (data: InfoAuth) => {
            setIsSubmitting(false);
            setUrl(data.continue_url || 'No URL returned');
          },
        }),
      );
    },
    [onError, user, dispatch],
  );

  return {
    form,
    onSubmit,
    url,
    isSubmitting,
  };
}
